import React, { FC, useEffect, useMemo } from 'react';
import { Field, FieldProps } from 'formik';
import { Element } from 'react-scroll';
import Card from 'components/UI/Card';
import InputAutoSave from 'components/UI/InputAutoSave';
import ToggleSwitch from 'components/UI/ToggleSwitch';
import TagAutoSave from 'components/UI/TagAutoSave';
import getErrorText from 'utils/getErrorText';
import { useUserTheme } from 'domains/user/model/selectors';
import { getCurrentVariableField } from 'utils/getCurrentVariableField';
import { createOptionsForConfigSelect } from 'utils/createOptionsForConfigSelect';
import { checkedLocalization } from 'utils/checkedLocalization';
import { FieldLength } from 'shared/consts';
import NewSelect from 'components/UI/NewSelect';
import { useDetectBlockScroll } from 'utils/useDetectBlockScroll';
import {
  CampaignGeneralComponentProps,
  RGetPersonal,
  TExperimentalInfoField,
} from 'domains/campaign/types';
import { useDispatchApp } from 'redux/rootSelectors';
import { fetchGridCampaignForContract } from 'domains/campaign/model/actions';
import { useIsGroup } from 'domains/user/hooks';
import i18n from '../../../../../../i18n';
import LabelField from '../../../../../../components/UI/LabelField';
import Help from '../../../../../../components/Help';
import css from '../styles.module.scss';
import { selectDefault } from '../../../../../../utils/selectDefault';
import { isFieldInObject } from '../../../../../../utils/isFieldInObject';
import { SelectField } from '../../../../../../types/general';
import { FetchedDataObj } from '../../../../../../redux/fetchedData';
import PartnerField from './CampaignFields/PartnerField';
import OfferField from './CampaignFields/OfferField';

type CampaignProps = Pick<
  CampaignGeneralComponentProps,
  'formik' | 'autoSubmit' | 'savedField' | 'setField' | 'handlerInput'
> & {
  campaign: RGetPersonal | null;
  clients: SelectField[];
  handlerUpdateTags: (value: string[]) => void;
  tags: string[] | null;
  handlerRevertTags?: () => void;
  isSavedTags?: string[];
  LTUPastTags?: number;
  errorTags?: FetchedDataObj<string[]>['error'];
  clientDisable: boolean;
  titleAutoFocus?: boolean;
  locked?: boolean;
  experimentalInfo?: TExperimentalInfoField[] | null;
  offersOptions?: SelectField[];
};

const Campaign: FC<CampaignProps> = ({
  autoSubmit,
  formik,
  campaign,
  handlerRevertTags,
  isSavedTags,
  errorTags,
  handlerUpdateTags,
  tags,
  handlerInput,
  LTUPastTags,
  savedField,
  clients,
  setField,
  clientDisable,
  titleAutoFocus,
  locked,
  experimentalInfo,
  offersOptions,
}) => {
  const { getValueFromSettingForKey } = useUserTheme();
  const { isOwner } = useIsGroup();
  const dispatch = useDispatchApp();
  const categoriesCampaign = useMemo(
    () => getCurrentVariableField(experimentalInfo, 'categories_campaign'),
    [experimentalInfo],
  );

  const categoriesOptions = useMemo(
    () => createOptionsForConfigSelect(categoriesCampaign),
    [categoriesCampaign],
  );

  const { endScrollingBlock } = useDetectBlockScroll(true);

  useEffect(() => {
    if (formik.values.contract_uuid && isOwner) {
      dispatch(
        fetchGridCampaignForContract({
          status: ['ACTIVE'],
          contract_uuid: formik.values.contract_uuid,
        }),
      );
    }
  }, [formik.values.contract_uuid]);

  return (
    <Card
      noHover
      style={{ padding: '28px 24px' }}
      className={css.card}
      isUseAccess
    >
      <h3>
        {i18n.t('campaigns_page.campaign_settings.Campaign_name')}{' '}
        {getValueFromSettingForKey('HideHelp') && (
          <Help
            width={200}
            title={i18n.t('campaigns_page.campaign_settings.Campaign_name')}
            helpattributes={{
              link: i18n.t(
                'campaigns_page.campaign_settings.campaign_prompt.main.link',
              ),
              sizeIcon: 18,
            }}
          />
        )}
      </h3>
      <div className={css.form}>
        <PartnerField
          formik={formik}
          clients={clients}
          clientDisable={clientDisable}
          autoSubmit={autoSubmit}
        />
        <InputAutoSave
          label={i18n.t('campaigns_page.campaign_settings.Advertiser_name')}
          defaultValue={campaign?.advertiser_name}
          value={formik.values.advertiser_name}
          name="advertiser_name"
          placeholder={i18n.t(
            'campaigns_page.campaign_settings.Advertiser_name',
          )}
          maxLength={FieldLength.advertiserName}
          setFieldValue={formik.setFieldValue}
          handlerInputEnter={handlerInput}
          isError={!!formik.errors.advertiser_name}
          errorText={formik.errors.advertiser_name}
          isSaved={isFieldInObject(savedField, 'advertiser_name')}
          isDisable={locked}
        />
        <Element name="title">
          <InputAutoSave
            name="title"
            label={i18n.t('campaigns_page.create_campaign.name')}
            defaultValue={campaign?.title}
            value={formik.values.title}
            maxLength={FieldLength.campaignTitle}
            handlerInputEnter={handlerInput}
            setFieldValue={formik.setFieldValue}
            isError={!!formik.errors.title}
            errorText={formik.errors.title}
            isSaved={isFieldInObject(savedField, 'title')}
            autoFocus={titleAutoFocus}
            isDisable={locked}
            placeholder={i18n.t('campaigns_page.create_campaign.name')}
          />
        </Element>
        {campaign && (
          <LabelField label={i18n.t('campaigns_page.create_campaign.status')}>
            {campaign?.status === 'ARCHIVE' ? (
              <ToggleSwitch
                hideToggleBgActive
                activateText={i18n.t('clients_page.archive_clients')}
                onChange={() => {
                  setField(
                    'status',
                    formik.values.status !== 'STOPPED' ? 'STOPPED' : 'ARCHIVE',
                  );
                }}
                checked={formik.values.status !== 'STOPPED'}
                disabled={locked}
              />
            ) : (
              <ToggleSwitch
                onChange={() => {
                  setField(
                    'status',
                    formik.values.status !== 'STOPPED' ? 'STOPPED' : 'LAUNCHED',
                  );
                }}
                checked={formik.values.status !== 'STOPPED'}
                disabled={locked}
              />
            )}
          </LabelField>
        )}
        <OfferField
          formik={formik}
          offersOptions={offersOptions}
          autoSubmit={autoSubmit}
        />
        {categoriesCampaign && (
          <LabelField
            label={checkedLocalization(
              `campaigns_page.create_campaign.${categoriesCampaign.field}`,
              categoriesCampaign.title,
            )}
          >
            <Field name={`config_values.${categoriesCampaign.id}`}>
              {({ field, form }: FieldProps) => (
                <NewSelect
                  autoSubmit={autoSubmit}
                  form={form}
                  field={field}
                  isClearable
                  selectAttributes={{
                    options: categoriesOptions,
                    placeholder: i18n.t(`project_page.forms.not_selected`),
                    value:
                      formik.values.config_values &&
                      categoriesOptions &&
                      selectDefault(
                        String(
                          formik.values.config_values[
                            String(categoriesCampaign.id)
                          ],
                        ),
                        categoriesOptions,
                      ),
                    isClearable: true,
                    closeMenuOnScroll:
                      !endScrollingBlock && endScrollingBlock !== null
                        ? () => true
                        : () => false,
                  }}
                  heightCustom={32}
                  viewStyle="Horizontal"
                />
              )}
            </Field>
          </LabelField>
        )}
        {tags && (
          <TagAutoSave
            tagsData={tags}
            handlerData={handlerUpdateTags}
            handleRevert={handlerRevertTags}
            LTUPast={LTUPastTags}
            isSaved={isSavedTags}
            isError={errorTags?.isError}
            isErrorTags={errorTags?.fields}
            errorText={getErrorText(errorTags?.code)}
            theme="horizontal"
            isDisable={locked}
          />
        )}
      </div>
    </Card>
  );
};

export default React.memo(Campaign);
